'use client';

import { useState, type JSX } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InlineHelpTooltip } from '@/components/ui/tooltip';
import { COUNTRY_KEYS } from '@/lib/constants';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface ContactPersonProps {
  formData: {
    countryOfResidence: string;
    personalId: string;
    otherCitizenship: boolean;
    citizenshipCountries: string;
    firstName: string;
    lastName: string;
    companyName: string;
    careOf: string;
    streetAddress: string;
    postalCode: string;
    city: string;
    phone: string;
    mobile: string;
    email: string;
    [key: string]: string | boolean;
  };
  onChange: (section: string, field: string, value: string | boolean) => void;
}

const OPTIONAL_CONTACT_PERSON_FIELDS = new Set(['citizenshipCountries', 'careOf', 'mobile']);
const PERSONAL_ID_PATTERN = /^\d{8}-\d{4}$/;
const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const POSTAL_CODE_PATTERN = /^\d{5}$/;

function getContactPersonFieldError(
  field: string,
  value: string | boolean,
  t: ReturnType<typeof useLanguage>['t'],
): string | null {
  if (OPTIONAL_CONTACT_PERSON_FIELDS.has(field) || typeof value !== 'string') {
    return null;
  }

  if (value.trim() === '') {
    return t.fieldRequired;
  }

  switch (field) {
    case 'firstName':
    case 'lastName':
      return value.length < 2 ? t.contactNameMinLength : null;
    case 'personalId':
      return PERSONAL_ID_PATTERN.test(value) ? null : 'Invalid personal ID format (YYYYMMDD-XXXX)';
    case 'phone':
      return value.trim() === '' ? t.phoneRequired : null;
    case 'email':
      return EMAIL_PATTERN.test(value) ? null : t.invalidEmail;
    case 'streetAddress':
      return value.length < 5 ? t.addressMinLength : null;
    case 'postalCode':
      return POSTAL_CODE_PATTERN.test(value) ? null : t.invalidPostalCode;
    default:
      return null;
  }
}

export default function ContactPersonInfoPage({
  formData,
  onChange,
}: Readonly<ContactPersonProps>): JSX.Element {
  const { t } = useLanguage();
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string | boolean): string | null => {
    const error = getContactPersonFieldError(field, value, t);

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  const markFieldAsDirty = (field: string): void => {
    setDirtyFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field: string): void => {
    markFieldAsDirty(field);
    const value = formData[field];
    if (typeof value === 'string') {
      validateField(field, value);
    }
  };

  const shouldShowError = (field: string): boolean => {
    return Boolean(dirtyFields[field] && errors[field]);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">{t.contactPersonTitle}</h3>
      <p className="text-sm text-gray-500">{t.contactPersonDesc}</p>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="countryOfResidence">{t.registeredIn}</Label>
          <Select
            value={formData.countryOfResidence}
            onValueChange={(value) => onChange('contactPerson', 'countryOfResidence', value)}
          >
            <SelectTrigger id="countryOfResidence" className="w-full">
              <SelectValue placeholder={t.countryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_KEYS.map((countryKey) => (
                <SelectItem key={countryKey} value={countryKey}>
                  {t.countries[countryKey as keyof typeof t.countries]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="personalId">{t.personalId}</Label>
          <Input
            id="personalId"
            placeholder="YYYYMMDD-XXXX"
            value={formData.personalId}
            onChange={(e) => onChange('contactPerson', 'personalId', e.target.value)}
            onBlur={() => handleBlur('personalId')}
            className={shouldShowError('personalId') ? 'border-red-500' : ''}
          />
          {shouldShowError('personalId') && (
            <p className="text-sm text-red-500">{errors.personalId}</p>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="otherCitizenship"
            checked={formData.otherCitizenship}
            onCheckedChange={(checked) =>
              onChange('contactPerson', 'otherCitizenship', Boolean(checked))
            }
          />
          <Label htmlFor="otherCitizenship">{t.citizenshipOtherCountry}</Label>
        </div>

        {formData.otherCitizenship && (
          <div className="space-y-2">
            <Label htmlFor="citizenshipCountries">{t.specifyCountries}</Label>
            <Input
              id="citizenshipCountries"
              value={formData.citizenshipCountries}
              onChange={(e) => onChange('contactPerson', 'citizenshipCountries', e.target.value)}
              onBlur={() => handleBlur('citizenshipCountries')}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="firstName" className="flex items-center">
            {t.firstName} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => onChange('contactPerson', 'firstName', e.target.value)}
            onBlur={() => handleBlur('firstName')}
            className={shouldShowError('firstName') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('firstName') && (
            <p className="text-sm text-red-500">{errors.firstName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName" className="flex items-center">
            {t.lastName} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => onChange('contactPerson', 'lastName', e.target.value)}
            onBlur={() => handleBlur('lastName')}
            className={shouldShowError('lastName') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('lastName') && <p className="text-sm text-red-500">{errors.lastName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyName" className="flex items-center gap-1">
            {t.companyOrderer}
            <InlineHelpTooltip label={t.companyOrdererHelp} />
          </Label>
          <Input
            id="companyName"
            value={formData.companyName}
            onChange={(e) => onChange('contactPerson', 'companyName', e.target.value)}
            onBlur={() => handleBlur('companyName')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="careOf">{t.careOf}</Label>
          <Input
            id="careOf"
            value={formData.careOf}
            onChange={(e) => onChange('contactPerson', 'careOf', e.target.value)}
            onBlur={() => handleBlur('careOf')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="streetAddress" className="flex items-center">
            {t.streetAddress} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="streetAddress"
            value={formData.streetAddress}
            onChange={(e) => onChange('contactPerson', 'streetAddress', e.target.value)}
            onBlur={() => handleBlur('streetAddress')}
            className={shouldShowError('streetAddress') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('streetAddress') && (
            <p className="text-sm text-red-500">{errors.streetAddress}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="postalCode" className="flex items-center">
            {t.postalCode} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="postalCode"
            value={formData.postalCode}
            onChange={(e) => onChange('contactPerson', 'postalCode', e.target.value)}
            onBlur={() => handleBlur('postalCode')}
            className={shouldShowError('postalCode') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('postalCode') && (
            <p className="text-sm text-red-500">{errors.postalCode}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="city" className="flex items-center">
            {t.city} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => onChange('contactPerson', 'city', e.target.value)}
            onBlur={() => handleBlur('city')}
            className={shouldShowError('city') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('city') && <p className="text-sm text-red-500">{errors.city}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="flex items-center">
            {t.phoneNumber} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => onChange('contactPerson', 'phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            className={shouldShowError('phone') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('phone') && <p className="text-sm text-red-500">{errors.phone}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="mobile">{t.mobile}</Label>
          <Input
            id="mobile"
            value={formData.mobile}
            onChange={(e) => onChange('contactPerson', 'mobile', e.target.value)}
            onBlur={() => handleBlur('mobile')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="flex items-center">
            {t.emailAddress} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => onChange('contactPerson', 'email', e.target.value)}
            onBlur={() => handleBlur('email')}
            className={shouldShowError('email') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('email') && <p className="text-sm text-red-500">{errors.email}</p>}
        </div>
      </div>
    </div>
  );
}
